/** #1232: exact owned exposure details using the Tracker's existing pooled
 * read and action handlers. No catalogue guessing, network read or new ticket. */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GameDetailLink } from '@/components/atoms/GameDetailLink';
import { OrderActionButtons } from '@/components/molecules/OrderActionButtons';
import { InfoTooltip } from '@/components/cards/cardKit';
import type { OrderCardVM } from '@/components/cards/orderCardModel';
import type { PositionCardVM } from '@/components/cards/positionCardModel';
import type { UsOrderRow, UsPositionRow } from '@/lib/polymarketUs/types';
import type { UsAccountView, UsTrackerRead } from './usTrackerFeed';
import { UsCloseControl, usOrderActionProps, type UsRowDispatch, type UsTrackerMarkets } from './usTrackerRows';
import { usGameDetailHref } from './usGameLink';
import { usInventorySelection, withoutUsInventorySelection } from './usInventoryLink';

interface Props {
  read: UsTrackerRead | null; sessionKey: string; markets: UsTrackerMarkets; dispatch: UsRowDispatch;
  orderVM(view: UsAccountView, order: UsOrderRow, sport: null): OrderCardVM;
  positionVM(view: UsAccountView, position: UsPositionRow, sport: null): PositionCardVM;
}
export function UsInventoryDetail({ read, sessionKey, markets, dispatch, orderVM, positionVM }: Props) {
  const [params, setParams] = useSearchParams();
  const selected = useMemo(() => usInventorySelection(params), [params]);
  const selectionKey = selected ? `${selected.accountId}:${selected.marketSlug}:${selected.outcome}` : '';
  const heading = useRef<HTMLHeadingElement>(null);
  const initialSession = useRef(sessionKey);
  const dismiss = useCallback(() => setParams(withoutUsInventorySelection(params), { replace: true }), [params, setParams]);
  useEffect(() => {
    if (initialSession.current === sessionKey) return;
    initialSession.current = sessionKey;
    if (selected) dismiss();
  }, [sessionKey, selected, dismiss]);
  useEffect(() => {
    if (!selectionKey) return;
    heading.current?.focus({ preventScroll: true });
    heading.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectionKey]);
  useEffect(() => {
    if (!selected) return;
    const key = (event: KeyboardEvent) => {
      // Existing confirmation/Close windows own Escape while they are open.
      if (event.key === 'Escape' && !document.querySelector('[role="dialog"]')) dismiss();
    };
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  }, [selected, dismiss]);
  if (!selected) return null;
  const view = read?.accounts.find(a => a.accountId === selected.accountId);
  const accessible = view?.context?.permissions.viewOwn && !view.openError;
  const matches = (row: UsOrderRow | UsPositionRow) => row.target.marketSlug === selected.marketSlug && row.target.outcome === selected.outcome;
  const orders = accessible ? view.workingOrders.filter(matches) : [];
  const positions = accessible ? view.openPositions.filter(matches) : [];
  const href = accessible ? usGameDetailHref(markets.classifier.game(selected.marketSlug), selected.outcome, selected.accountId) : null;
  const pending = !read || read.list.state === 'loading' || view?.opening;
  return <section aria-label="Polymarket US market details" data-testid="us-inventory-detail"
    data-market-slug={selected.marketSlug} data-account-id={selected.accountId} data-outcome={selected.outcome}
    className="border-border bg-card m-4 max-h-[45vh] shrink-0 overflow-y-auto rounded-lg border p-4">
    <div className="mb-2 flex items-start justify-between gap-4">
      <h2 ref={heading} tabIndex={-1} className="text-sm font-semibold">Polymarket US · {selected.outcome.toUpperCase()}</h2>
      <button type="button" className="text-muted-foreground text-xs underline" aria-label="Close market details" onClick={dismiss}>Close details</button>
    </div>
    {pending ? <p role="status">Reading your saved activity…</p> : !accessible ?
      <p role="status">This account is not available to this login.</p> : <>
      <div className="text-muted-foreground mb-2 flex flex-wrap items-center gap-1.5 text-xs">
        {href ? <>Game details are now available. <GameDetailLink href={href} /></>
          : <span>Not on the current game board · New orders unavailable</span>}
        {view.label && <span>{view.label}</span>}
        <InfoTooltip title="Saved market activity">
          {!href && <p className="mb-2">Your saved orders and holdings remain available here. New orders need an exact live market; no other market is substituted.</p>}
          <p className="break-words">Market: {selected.marketSlug}</p>
          <p className="break-all">Account: {view.accountId}</p>
        </InfoTooltip>
      </div>
      {(view.readError || view.loading || read?.list.state === 'failed') && <p role="status" className="mb-2 text-xs text-amber-400">
        {view.readError || read?.list.state === 'failed' ? 'The latest account read failed. These are the last known rows.' : 'Refreshing saved activity…'}
      </p>}
      {orders.length === 0 && positions.length === 0 && <p role="status" className="text-sm">No open order or holding for this outcome remains in the current account read.</p>}
      {orders.map(order => {
        const vm = orderVM(view, order, null), facts = vm.us!, actions = usOrderActionProps(facts, dispatch);
        return <div key={order.intentId} data-testid="us-inventory-order" className="border-border mb-2 flex flex-wrap items-center justify-between gap-3 rounded border p-3 text-xs">
          <div className="min-w-0 break-words"><strong>{facts.marketLabel}</strong><p>{order.action === 'sell' ? 'Sell' : 'Buy'} {selected.outcome.toUpperCase()} · {vm.remainingText ?? '—'} remaining · {vm.priceText ?? '—'}</p>
            {facts.statusText && <p title={facts.statusTitle}>{facts.statusText}</p>}</div>
          {actions && <OrderActionButtons {...actions} />}
        </div>;
      })}
      {positions.map(position => {
        const vm = positionVM(view, position, null), facts = vm.us!;
        return <div key={`${position.target.marketSlug}:${position.target.outcome}`} data-testid="us-inventory-position" className="border-border mb-2 flex flex-wrap items-center justify-between gap-3 rounded border p-3 text-xs">
          <div className="min-w-0 break-words"><strong>{facts.marketLabel}</strong><p>{selected.outcome.toUpperCase()} · {vm.qtyText ?? '—'} held</p>
            {facts.partialText && <p title={facts.partialTitle ?? undefined}>{facts.partialText}</p>}</div>
          <UsCloseControl facts={facts} onClose={() => dispatch.close(facts)} />
        </div>;
      })}
    </>}
  </section>;
}
